import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTaskMutations } from '@/hooks/useInternalTasks';
import type { TaskPriority, TaskProject } from '@/../../shared/internal-tasks';
import { AssigneeSelect, Dot, PrioritySelect } from './task-fields';
import { useTranslation } from 'react-i18next';

export function NewTaskDialog({ open, onOpenChange, projects, defaultProjectId }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: TaskProject[];
  defaultProjectId?: string;
}) {
  const { t } = useTranslation();
  const { createTask } = useTaskMutations();
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [assignee, setAssignee] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (open) {
      setProjectId(defaultProjectId ?? projects[0]?.projectId ?? '');
      setTitle(''); setPriority('medium'); setAssignee(null); setDueDate('');
    }
  }, [open, defaultProjectId, projects]);

  const canSave = !!projectId && title.trim().length > 0 && !createTask.isPending;

  const submit = () => {
    if (!canSave) return;
    createTask.mutate(
      {
        projectId,
        title: title.trim(),
        priority,
        assigneeEmail: assignee ?? undefined,
        dueDate: dueDate || undefined,
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{t('internal.newtaskdialog.nueva_tarea')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Proyecto</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-9"><SelectValue placeholder={t('internal.newtaskdialog.elegi_un_proyecto')} /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.projectId} value={p.projectId}>
                    <span className="flex items-center gap-2"><Dot color={p.color} />{p.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('internal.newtaskdialog.titulo')}</Label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              placeholder={t('internal.newtaskdialog.que_hay_que_hacer')}
              maxLength={200}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Prioridad</Label>
              <PrioritySelect value={priority} onChange={setPriority} />
            </div>
            <div className="space-y-1.5">
              <Label>Vencimiento</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-8" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Responsable</Label>
            <AssigneeSelect value={assignee} onChange={setAssignee} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSave}>{createTask.isPending ? t('internal.newtaskdialog.creando') : t('internal.newtaskdialog.crear_tarea')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
